import React from 'react'

import type { GetMQTTResponse } from '@/api/mqtt.api'
import { getMqttValue } from '@/features/wizard-page/utils/deviceTemplate'
import type { DevicePowerState } from '@/features/wizard-page/utils/devicePower'
import type { DeviceTelemetryFreshness } from '@/features/dashboard/types'

import { updateAlertIntervals, type AlertInterval } from '../utils/chartIntervals'
import {
  appendLiveSample,
  createLiveSeries,
  openLiveGap,
  pruneLiveSeries,
  sealLiveSeries,
  type LiveSeries,
} from '../utils/liveHistory'

/** Częstotliwość domykania luk. Sekunda daje dokładność nieodróżnialną przy progu liczonym
 *  w minutach, a tyknięcia biegną także wtedy, gdy wykres jest zamknięty. */
const SEAL_INTERVAL_MS = 1000

export type LiveChartSource = { dataType: string; index: number }

export type DeviceLiveHistory = {
  seriesRef: React.MutableRefObject<Record<string, LiveSeries>>
  alarmsIntervalRef: React.MutableRefObject<AlertInterval[]>
  subscribe: (key: string, source: LiveChartSource) => void
}

/** Zbieranie historii serii live dla całej strony urządzenia.
 *
 *  Hook żyje tak długo, jak trasa urządzenia, więc zamknięcie modala wykresu nie przerywa
 *  zbierania ani nie kasuje historii. Modal jedynie zgłasza, którą serię chce oglądać. */
const useDeviceLiveHistory = (
  mqtt: GetMQTTResponse[],
  powerState: DevicePowerState,
  telemetryFreshness: DeviceTelemetryFreshness,
  alertStatus: string,
): DeviceLiveHistory => {
  const seriesRef = React.useRef<Record<string, LiveSeries>>({})
  const sourcesRef = React.useRef<Record<string, LiveChartSource>>({})
  const alarmsIntervalRef = React.useRef<AlertInterval[]>([])
  const isPoweredOffRef = React.useRef(powerState === 'off')
  const isTelemetryLiveRef = React.useRef(telemetryFreshness === 'live')

  isPoweredOffRef.current = powerState === 'off'
  isTelemetryLiveRef.current = telemetryFreshness === 'live'

  /* Świeżość czytana z refa, bo `subscribe` jest stabilnym `useCallback` bez zależności — domknięta
     wartość pochodziłaby z chwili montowania, a nie z chwili otwarcia wykresu. */
  const subscribe = React.useCallback((key: string, source: LiveChartSource) => {
    if (!key) return

    sourcesRef.current[key] = source
    if (!seriesRef.current[key]) {
      seriesRef.current[key] = createLiveSeries(Date.now(), isTelemetryLiveRef.current)
    }
  }, [])

  /* Stan alertu przychodzi gotowy z `toDeviceStatus`: przy świeżej telemetrii z ramki, inaczej ze
     stanu zapisanego w bazie. Wykres nie czyta ramki sam, bo miałby wtedy własną regułę i mógłby
     pokazać co innego niż kafelek nagłówka — zapasowy odczyt REST nie niesie ramki `alert`
     (`deviceMeasurementsLatest` trzyma wyłącznie surowe typy z MQTT), a ramka zachowana po
     zwietrzeniu strumienia mówi to, co było, a nie to, co jest. W obu wypadkach trwający alarm
     był zamykany i wykres wyłączonego urządzenia pokazywał sam przestój.

     Osobny efekt, bo stan alertu zmienia się niezależnie od ramek — dopisywanie próbek nie może
     reagować na coś, co nie jest nową wiadomością telemetryczną. */
  React.useEffect(() => {
    updateAlertIntervals(alarmsIntervalRef.current, alertStatus, new Date().toISOString())
  }, [alertStatus])

  React.useEffect(() => {
    /* Znacznik próbki z zegara przeglądarki, nie z ramki. Ramka niesie czas backendu, więc krawędź
       pasma alarmu rozjeżdżała się z ostatnim punktem linii o różnicę zegarów plus opóźnienie
       transmisji. Tym samym zegarem liczony jest próg bezczynności. */
    const receivedAt = Date.now()

    /* Urządzenie zgłoszone jako offline nie dostarcza wartości — brak dopisania próbki sprawia,
       że po progu bezczynności otworzy się luka. */
    if (String(mqtt.find((frame) => frame.type === 'alert')?.value ?? 'normal') === 'offline') return

    /* Wyłączone urządzenie może dalej nadawać ramki, ale kontrolki pokazują wtedy wartości
       resetowe. Pomijanie próbek utrzymuje lukę otwartą, więc wykres mówi to samo co kafelki. */
    if (isPoweredOffRef.current) return

    Object.entries(sourcesRef.current).forEach(([key, source]) => {
      const series = seriesRef.current[key]
      const frame = mqtt.find((item) => item.type === source.dataType)

      if (!series || !frame) return

      appendLiveSample(series, {
        t: receivedAt,
        value: getMqttValue(mqtt, source.dataType, source.index),
        sourceAt: String(frame.createdAt),
      })
    })
  }, [mqtt])

  React.useEffect(() => {
    if (powerState !== 'off') return

    const now = Date.now()
    Object.values(seriesRef.current).forEach((series) => openLiveGap(series, now))
  }, [powerState])

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now()

      Object.values(seriesRef.current).forEach((series) => {
        if (isPoweredOffRef.current) openLiveGap(series, now)
        else sealLiveSeries(series, now)

        pruneLiveSeries(series, now)
      })
    }, SEAL_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [])

  return React.useMemo(() => ({ seriesRef, alarmsIntervalRef, subscribe }), [subscribe])
}

export default useDeviceLiveHistory
